import React, { useEffect, useRef, useState } from "react";
import { UserInfo } from "./model/backendTypes";

// Props für das Profil-Dropdown:
// - user: aktuell eingeloggter Benutzer (Name/Org/E-Mail)
// - onLogout: Callback aus dem Parent (App/Plan), um den Logout auszulösen
type Props = {
  user: UserInfo | null;
  onLogout: () => void;
};

export default function ProfileDropdown({ user, onLogout }: Props) {
  // Referenz auf den Dropdown-Container:
  // wird für "Click außerhalb schließen" verwendet.
  const rootRef = useRef<HTMLDivElement | null>(null);

  // UI-State des Menüs.
  const [open, setOpen] = useState(false);

  // Schließt das Dropdown:
  // - bei Klick außerhalb
  // - bei Escape-Taste
  useEffect(() => {
    if (!open) return;

    const onDocumentClick = (event: MouseEvent) => {
      if (!rootRef.current) return;
      const target = event.target as Node;
      if (!rootRef.current.contains(target)) {
        setOpen(false);
      }
    };

    const onEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocumentClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    // Äußerer Wrapper, damit das absolute Dropdown relativ zum Profile-Button positioniert wird.
    <div className="profile-menu" ref={rootRef}>
      {/* Trigger-Button für Öffnen/Schließen des Menüs */}
      <button
        type="button"
        className="header-ghost-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        Profile
      </button>

      {open && (
        // Inhalt des Dropdown-Menüs (User-Infos + Logout).
        <div className="profile-menu__dropdown profile-menu__dropdown--compact" role="menu">
          {user && (
            // User-Block aus den Daten, die vom Parent übergeben werden.
            <div className="profile-menu__user">
              <div className="profile-menu__avatar">
                {(user.name?.[0] ?? user.email?.[0] ?? "?").toUpperCase()}
              </div>
              <div className="profile-menu__user-info">
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <div className="profile-menu__org">{user.organization_name}</div>
                  {user.organization_abo && (
                    <span style={{
                      fontSize: "0.6rem", 
                      fontWeight: 800, 
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      padding: "2px 5px", 
                      borderRadius: "4px", 
                      backgroundColor: "color-mix(in srgb, var(--heading-color) 15%, transparent)",
                      color: "var(--heading-color)",
                      flexShrink: 0
                    }}>
                      {user.organization_abo}
                    </span>
                  )}
                </div>
                <div className="profile-menu__meta">
                  {user.name} · {user.email}
                </div>
              </div>
            </div>
          )}

          {/* Logout-Bereich im Footer des Menüs */}
          <div className="profile-menu__footer">
            <button
              type="button"
              className="profile-menu__logout"
              onClick={() => {
                setOpen(false);
                onLogout();
              }}
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
